import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { formatMoney } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Truck, Ship, FlaskConical, Banknote, CalendarClock } from "lucide-react";
import { InventoryJourney } from "@/components/InventoryJourney";
import { ShipmentTrackingCard } from "@/components/containers/ShipmentTrackingCard";
import { SettlementOverviewCard } from "@/components/settlement/SettlementOverviewCard";

const PRIORITY_STYLE = {
  0: { label: "Critical", cls: "text-status-sold" },
  1: { label: "Action needed", cls: "text-terracotta" },
  2: { label: "In progress", cls: "text-charcoal" },
  3: { label: "Watch", cls: "text-warmgrey" },
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/dashboard/admin")
      .then((res) => setData(res.data))
      .catch((e) => {
        setError(true);
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      });
  }, []);

  if (error) {
    return (
      <Card className="border-border bg-white" data-testid="admin-dashboard-error">
        <CardContent className="p-16 text-center text-sm text-muted-foreground">Dashboard unavailable — please retry.</CardContent>
      </Card>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid="admin-dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  const { attention, snapshot } = data;
  const month = snapshot.this_month || {};
  const cards = [
    { testid: "op-card-orders", icon: Truck, value: snapshot.orders_to_fulfill ?? 0, label: "Orders to fulfill", to: "/admin/orders" },
    { testid: "op-card-samples", icon: FlaskConical, value: snapshot.samples_to_ship ?? 0, label: "Samples to ship", to: "/admin/samples" },
    { testid: "op-card-reservations", icon: CalendarClock, value: snapshot.active_reservations ?? 0, label: "Active reservations", to: "/admin/reservations" },
    { testid: "op-card-containers", icon: Ship, value: snapshot.containers_in_transit ?? 0, label: "Containers in transit", to: "/admin/containers" },
    { testid: "op-card-sales", icon: Banknote, value: formatMoney(snapshot.sales_this_month ?? 0), label: "Sales this month" },
    { testid: "op-card-inventory", icon: AlertTriangle, value: (snapshot.inventory_alerts || []).length, label: "Inventory alerts", to: "/admin/inventory", warn: (snapshot.inventory_alerts || []).length > 0 },
  ];

  return (
    <div data-testid="admin-dashboard">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Operations</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Dashboard</h1>

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6" data-testid="op-cards">
        {cards.map((c) => (
          <Card key={c.testid} data-testid={c.testid} onClick={c.to ? () => navigate(c.to) : undefined}
            className={`border-border bg-white ${c.to ? "cursor-pointer transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm" : ""}`}>
            <CardContent className="p-5">
              <c.icon className={`h-4 w-4 ${c.warn ? "text-status-sold" : "text-terracotta"}`} />
              <p className={`num mt-3 text-3xl font-light ${c.warn ? "text-status-sold" : "text-charcoal"}`} data-testid={`${c.testid}-value`}>{c.value}</p>
              <p className="text-xs text-muted-foreground">{c.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <InventoryJourney journey={snapshot.journey} />
      <div className="mt-8"><ShipmentTrackingCard /></div>

      {attention.length > 0 && (
        <div className="mt-8" data-testid="attention-queue">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Attention queue</p>
          <div className="mt-3 space-y-2">
            {attention.map((a, i) => (
              <Card key={i} className="cursor-pointer border-border bg-white transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm" data-testid={`attention-${a.key}-${i}`} onClick={() => navigate(a.link)}>
                <CardContent className="flex items-center gap-4 p-4">
                  {a.priority === 0 && <AlertTriangle className="h-4 w-4 shrink-0 text-status-sold" />}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-charcoal">{a.title}</p>
                    <p className="text-xs text-muted-foreground">{a.detail}</p>
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-wider ${PRIORITY_STYLE[a.priority].cls}`} data-testid={`attention-priority-${i}`}>
                    {PRIORITY_STYLE[a.priority].label}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8" data-testid="exec-strip">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Business to date</p>
        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Card className="border-border bg-white"><CardContent className="p-5">
            <p className="num text-2xl font-semibold text-charcoal" data-testid="sales-ytd">{formatMoney(snapshot.sales_ytd ?? 0)}</p>
            <p className="text-xs text-muted-foreground">Sales — year to date</p>
          </CardContent></Card>
          <Card className="border-border bg-white"><CardContent className="p-5">
            <p className="num text-2xl font-semibold text-charcoal" data-testid="sales-t12m">{formatMoney(snapshot.sales_t12m ?? 0)}</p>
            <p className="text-xs text-muted-foreground">Sales — trailing 12 months</p>
          </CardContent></Card>
        </div>
      </div>

      <div className="mt-8" data-testid="month-strip">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">This month</p>
        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <Card className="border-border bg-white"><CardContent className="p-5">
            <p className="num text-2xl font-light text-charcoal" data-testid="month-sales">{formatMoney(month.sales ?? 0)}</p>
            <p className="text-xs text-muted-foreground">Sales</p>
          </CardContent></Card>
          <Card className="border-border bg-white"><CardContent className="p-5">
            <p className="num text-2xl font-light text-charcoal" data-testid="month-orders">{month.orders ?? 0}</p>
            <p className="text-xs text-muted-foreground">Orders</p>
          </CardContent></Card>
          <Card className="border-border bg-white"><CardContent className="p-5">
            <p className="num text-2xl font-light text-charcoal" data-testid="month-dealers">{month.new_dealers ?? 0}</p>
            <p className="text-xs text-muted-foreground">New dealers</p>
          </CardContent></Card>
          <Card className="border-border bg-white"><CardContent className="p-5">
            <p className="num text-2xl font-light text-charcoal" data-testid="month-samples">{month.samples_shipped ?? 0}</p>
            <p className="text-xs text-muted-foreground">Samples shipped</p>
          </CardContent></Card>
          <Card className="border-border bg-white sm:col-span-3 lg:col-span-1"><CardContent className="p-5">
            <p className="truncate font-display text-lg font-light text-charcoal" data-testid="month-top-product">
              {month.top_product ? month.top_product.label : "—"}
            </p>
            <p className="text-xs text-muted-foreground">
              Top product{month.top_product ? ` · ${month.top_product.sqft.toLocaleString()} sq ft` : ""}
            </p>
          </CardContent></Card>
        </div>
      </div>
      <SettlementOverviewCard />
    </div>
  );
}
